<script lang="ts" setup>
import {
  Image,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
  Popup,
} from "vant";
import { usePrompts } from "@/stores/prompts";
import { useMate } from "@/stores/mate";
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useMateTrait } from "@/stores/mateTrait";
import { useRouter } from "vue-router";
import { useGenStyle } from "@/stores/genStyle";
import { featureExtraction } from "@/modules/api";
import { trim } from "@/modules/common";
import { useTask } from "@/stores/task";
import { isEditTraitAction } from "./Card/action";
import { watch } from "vue";
import i18n from "@/locales";
import { formatStyle } from "../Image/Tabs/initStyle";

const taskStore = useTask();
const { activeTaskId } = storeToRefs(taskStore);

const media = computed<TextImage>(
  () => taskStore.getMedia(activeTaskId.value) as TextImage
);

// State
const promptStore = usePrompts();
const mateStore = useMate();
const { currentMate } = storeToRefs(mateStore);
const prompts = computed(
  () =>
    (media.value && promptStore.getPromptListByText(media.value.prompt)) || []
);
const enableTextMode = ref(false);
const enableChangeImg = ref(false);
const enableAutoAddPrompt = ref(false);
const traitName = ref("");
// EventHandler
const trait = useMateTrait();
const router = useRouter();
const genStyle = useGenStyle();

let isEdit = false;
const onConfirm = async () => {
  if (traitName.value.length === 0)
    return showFailToast({
      message: i18n.global.t("Message.pleaseEnterFeatureName"),
    });

  const task = taskStore.getTask(activeTaskId.value)!;
  isEdit = isEditTraitAction(task, "emoji");

  const loadingToast = showLoadingToast({
    message: isEdit
      ? i18n.global.t("Message.overwritingOriginalFeature")
      : i18n.global.t("Message.generatingFeature"),
    duration: 0,
  });
  try {
    const entity = task.entity as TextImage;
    const {
      promptJson,
      mediaId,
      style,
      sourceId,
      mateTraitId,
      customStyle,
      mixStyle,
      mode,
    } = entity;
    let { prompt = "", negativePrompt = "" } = entity;

    const params = {
      name: traitName.value,
      mediaId: isEdit && !enableChangeImg.value ? sourceId : mediaId,
      prompt: ``,
      negativePrompt: ``,
      sourceId,
      mode,
      ...formatStyle({ style, customStyle, mixStyle }),
    };
    if (isEdit) {
      // 编辑特征
      params.prompt = prompt;
      params.negativePrompt = negativePrompt;
      await trait.updateTrait(mateTraitId!, params);
      entity.mateTrait = {
        ...entity.mateTrait!,
        ...params,
      };
    } else {
      // 新建特征
      let promptList: Array<"env" | "clothing" | "faceExpression" | "pose"> = [
        "env",
        "clothing",
        "faceExpression",
        "pose",
      ];
      if (promptJson) {
        const { env, clothing, faceExpression, pose } = promptJson;
        if (env && clothing && faceExpression && pose) {
          promptList.forEach((v) => {
            if (promptJson[v].prompt)
              prompt += `${trim(promptJson[v].prompt, ",")},`;
            if (promptJson[v].negativePrompt)
              negativePrompt += `${trim(promptJson[v].negativePrompt, ",")},`;
          });
        }
      }
      entity.mode = entity.mode || 1;
      if ([1, 8].includes(entity.mode)) {
        if (prompt) prompt = trim(prompt, ",") + ",";
        if (
          media.value.customStyle?.version !== 2 &&
          enableAutoAddPrompt.value
        ) {
          const { data } = await featureExtraction(mediaId);
          if (data.prompt) prompt += trim(data.prompt, ",") + ",";
        }
        params.prompt = trim(prompt, ",");
        params.negativePrompt = trim(negativePrompt, ",");
      }
      await trait.createTrait(params);
    }
    await taskStore.like(activeTaskId.value);
    showSuccessToast({
      message: isEdit
        ? i18n.global.t("Message.overlaySuccess")
        : i18n.global.t("Message.creationSuccessful"),
    });
    router.push({ name: "imagePaintWithMode", query: { mode: 2 } });
    taskStore.showCreateTraitModal = false;
  } finally {
    loadingToast.close();
  }
};

watch(
  () => taskStore.showCreateTraitModal,
  (val) => {
    if (val) {
      traitName.value = "";

      const task = taskStore.getTask(activeTaskId.value)!;
      isEdit = isEditTraitAction(task, "emoji");
      if (isEdit) {
        traitName.value = (task.entity as TextImage).mateTrait?.name || "";
      }
    }
  }
);
</script>
<template>
  <popup
    position="bottom"
    v-model:show="taskStore.showCreateTraitModal"
    class="min-h-[80%] grid grid-rows-[1fr]"
  >
    <div
      v-if="taskStore.showCreateTraitModal"
      class="w-full h-full grid grid-rows-[1fr_auto] p-8 gap-4 pb-4"
    >
      <div class="flex flex-col gap-4">
        <div class="text-base text-center">
          {{ currentMate!.name }}
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $t("trait.traitName") }}</span>
            <input v-model="traitName" class="input input-bordered" />
          </div>
          <div class="flex flex-col gap-2">
            <span class="font-bold">{{ $t("trait.traitDescription") }}</span>
            <div v-if="!enableTextMode" class="flex gap-1 flex-wrap py-2">
              <span
                v-for="p in prompts"
                :key="p.id"
                class="badge badge-neutral mb-1"
              >
                {{ p.displayText }}
              </span>
            </div>
            <div v-else class="p-2 text-base-content/50">
              {{ media.prompt }}
            </div>
            <div class="flex justify-end gap-1 text-base-content/50">
              {{ $t("Texts.displayText") }}
              <input
                type="checkbox"
                class="toggle toggle-sm"
                v-model="enableTextMode"
              />
            </div>
          </div>
          <div class="flex flex-col gap-2 mt-2">
            <div class="flex items-center justify-between">
              <span class="font-bold">{{ $t("trait.traitCover") }}</span>
              <div
                v-if="isEdit"
                class="flex justify-end gap-1 text-base-content/50"
              >
                {{ $t("trait.changeCover") }}
                <input
                  type="checkbox"
                  class="toggle toggle-sm"
                  v-model="enableChangeImg"
                />
              </div>
              <div
                v-else-if="[1,8].includes(media.mode!) && media?.customStyle?.version !== 2"
                class="flex justify-end gap-1 text-base-content/50 flex-1"
              >
                {{ $t("trait.autoPrompt") }}
                <input
                  type="checkbox"
                  class="toggle toggle-sm"
                  v-model="enableAutoAddPrompt"
                />
              </div>
            </div>
            <div class="flex justify-center">
              <Image
                class="w-40 rounded overflow-hidden"
                :src="media.media?.url!"
                crossorigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="flex gap-2">
        <button class="btn btn-sm btn-primary flex-1" @click="onConfirm">
          {{ $t("Buttons.save") }}
        </button>
        <button
          class="btn btn-sm flex-1"
          @click="taskStore.showCreateTraitModal = false"
        >
          {{ $t("Buttons.back") }}
        </button>
      </div>
    </div>
  </popup>
</template>
